"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionLinkState = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const page_state_1 = require("./page.state");
/**
 * State for linking a collection to a vector field.
 *
 * Pending two-way links and bidirectional unlinks are tracked as arrays
 * to support multiple span link/unlink operations within a single edit session.
 */
class CollectionLinkState {
    constructor() {
        this.fieldId = null;
        this.collectionRevision = null;
        this.sourceFormRevision = null;
        this.isSpanLinkContext = false;
        this.pendingTwoWayLinks = mobx_1.observable.array([], { deep: false });
        this.pendingBidirectionalUnlinks = mobx_1.observable.array([], { deep: false });
    }
    get isLinking() {
        return this.fieldId != null;
    }
    get hasPendingTwoWayLink() {
        return this.pendingTwoWayLinks.length > 0;
    }
    get hasPendingBidirectionalUnlink() {
        return this.pendingBidirectionalUnlinks.length > 0;
    }
    beginLinking(fieldId, options) {
        var _a, _b;
        page_state_1.CurrentState.cancelInteractions();
        this.fieldId = fieldId;
        this.sourceFormRevision = (_a = options === null || options === void 0 ? void 0 : options.formRevision) !== null && _a !== void 0 ? _a : null;
        this.isSpanLinkContext = (_b = options === null || options === void 0 ? void 0 : options.isSpanContext) !== null && _b !== void 0 ? _b : false;
    }
    cancelLinking() {
        this.fieldId = null;
        this.collectionRevision = null;
        this.sourceFormRevision = null;
        this.isSpanLinkContext = false;
    }
    completeLinking(collection) {
        this.fieldId = null;
        this.collectionRevision = collection.revision;
    }
    addPendingTwoWayLink(link) {
        this.pendingTwoWayLinks.push(link);
    }
    /**
     * Remove only the pending two-way links where the source collection matches.
     * Used by the topo editor when discarding a single pole's changes, so that
     * pending links from other poles are preserved.
     */
    clearPendingTwoWayLinksForSource(sourceCollectionId) {
        for (let i = this.pendingTwoWayLinks.length - 1; i >= 0; i--) {
            const link = this.pendingTwoWayLinks[i];
            if (link.sourceCollection.id === sourceCollectionId) {
                this.pendingTwoWayLinks.remove(link);
            }
        }
    }
    /**
     * Clear all pending two-way links without reverting edit state.
     * Used after successful save — the proxy state has been serialized,
     * so we just need to drop the tracking references.
     */
    clearPendingTwoWayLinks() {
        this.pendingTwoWayLinks.clear();
    }
    /**
     * Discard all pending two-way links, reverting each unique target
     * collection's edit state. Called when the user discards changes
     * on the source collection (Pole A).
     */
    discardPendingTwoWayLinks() {
        const targets = new Set();
        for (const link of this.pendingTwoWayLinks) {
            targets.add(link.targetCollection);
        }
        for (const target of targets) {
            if (target.isEditing) {
                target.finishEdit();
            }
        }
        this.pendingTwoWayLinks.clear();
    }
    /**
     * Find and remove a pending two-way link by target collection and span instance.
     * Used when unlinking an unsaved span link on the map page.
     *
     * If the removed entry was the last pending link to this target collection,
     * finishEdit() is called to revert the target's proxy state. If other
     * pending links to the same target remain, finishEdit() is NOT called.
     */
    findAndRemovePendingTwoWayLink(targetCollection, targetSpanInstance) {
        const index = this.pendingTwoWayLinks.findIndex((link) => link.targetCollection === targetCollection && link.targetSpanInstance === targetSpanInstance);
        if (index === -1) {
            return null;
        }
        const [removed] = this.pendingTwoWayLinks.splice(index, 1);
        const hasOtherLinksToSameTarget = this.pendingTwoWayLinks.some((link) => link.targetCollection === targetCollection);
        if (!hasOtherLinksToSameTarget && targetCollection.isEditing) {
            targetCollection.finishEdit();
        }
        return removed !== null && removed !== void 0 ? removed : null;
    }
    addPendingBidirectionalUnlink(unlink) {
        this.pendingBidirectionalUnlinks.push(unlink);
    }
    /**
     * Remove only the pending bidirectional unlinks where the source collection matches.
     * Used by the topo editor when discarding a single pole's changes, so that
     * pending unlinks from other poles are preserved.
     */
    clearPendingBidirectionalUnlinksForSource(sourceCollectionId) {
        for (let i = this.pendingBidirectionalUnlinks.length - 1; i >= 0; i--) {
            const unlink = this.pendingBidirectionalUnlinks[i];
            if (unlink.sourceCollectionId === sourceCollectionId) {
                this.pendingBidirectionalUnlinks.remove(unlink);
            }
        }
    }
    /**
     * Clear all pending bidirectional unlinks without reverting edit state.
     * Used after successful save — the proxy state has been serialized,
     * so we just need to drop the tracking references.
     */
    clearPendingBidirectionalUnlinks() {
        this.pendingBidirectionalUnlinks.clear();
    }
    /**
     * Discard all pending bidirectional unlinks, reverting each unique target
     * collection's edit state. Called when the user discards changes
     * on the source collection.
     */
    discardPendingBidirectionalUnlinks() {
        const targets = new Set();
        for (const unlink of this.pendingBidirectionalUnlinks) {
            targets.add(unlink.targetCollection);
        }
        for (const target of targets) {
            if (target.isEditing) {
                target.finishEdit();
            }
        }
        this.pendingBidirectionalUnlinks.clear();
    }
}
exports.CollectionLinkState = CollectionLinkState;
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "fieldId", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "collectionRevision", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "sourceFormRevision", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionLinkState.prototype, "isSpanLinkContext", void 0);
tslib_1.__decorate([
    mobx_1.computed
], CollectionLinkState.prototype, "isLinking", null);
tslib_1.__decorate([
    mobx_1.computed
], CollectionLinkState.prototype, "hasPendingTwoWayLink", null);
tslib_1.__decorate([
    mobx_1.computed
], CollectionLinkState.prototype, "hasPendingBidirectionalUnlink", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "beginLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "cancelLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "completeLinking", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "addPendingTwoWayLink", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "clearPendingTwoWayLinksForSource", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "clearPendingTwoWayLinks", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "discardPendingTwoWayLinks", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "findAndRemovePendingTwoWayLink", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "addPendingBidirectionalUnlink", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "clearPendingBidirectionalUnlinksForSource", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "clearPendingBidirectionalUnlinks", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionLinkState.prototype, "discardPendingBidirectionalUnlinks", null);
//# sourceMappingURL=collection.link.state.js.map